import cx from 'classnames';
import type {FC} from 'react';
import {useSelector} from 'react-redux';
import {
  type FiltersStoreState,
  selectActiveFilterCountBySection,
} from '~/GalleryPB/reducers/filters';
import type {FilterGroupType} from '~/GalleryPB/types/filters';
import {useIsLargeAndUp} from '~library/hooks/useMatchQuery';
import {Accordion} from '~library/molecules/Accordion';
import {charcoal} from '~libSass/base/_exports.sass';
import {Filter} from './Filter';
import * as styles from './FilterGroup.module.scss';

interface FilterGroupProps {
  groupKey: string;
  data: FilterGroupType;
  isOpen: boolean;
  onToggleGroup: (groupKey: string) => void;
  onSelectFilter: (filterSectionName: string, filterId: string, newState: boolean) => void;
}

export const FilterGroup: FC<FilterGroupProps> = ({
  groupKey,
  data,
  isOpen,
  onToggleGroup,
  onSelectFilter,
}) => {
  const isLargeAndUp = useIsLargeAndUp();
  const activeFilterCountForGroup = useSelector((state: {filters: FiltersStoreState}) =>
    selectActiveFilterCountBySection(state, groupKey)
  );
  const isColorFilter = ['color', 'colors'].includes(data.display_name.toLowerCase());

  if (Object.keys(data.filters).length === 0) return null;

  let label = data.display_name;
  if (groupKey === 'show_only' && window.cards_page) label = 'Card type';

  if (activeFilterCountForGroup > 0) {
    label += ` (${activeFilterCountForGroup})`;
  }
  return (
    <Accordion
      key={groupKey}
      className={styles.filterGroupAccordion}
      label={label}
      labelVariant={isLargeAndUp ? 'paragraph2' : 'paragraph1'}
      labelClassName={styles.filterGroupLabel}
      uniqueAccordionBodyWrapperId={`accordionBodyWrapper_${groupKey}`}
      isOpen={isOpen}
      onOpen={() => onToggleGroup(groupKey)}
      onClose={() => onToggleGroup(groupKey)}
      chevronColor={charcoal}
    >
      <div className={cx(styles.filterGroup, {[styles.colorFilterGroup]: isColorFilter})}>
        {Object.entries(data.filters).map(([filterKey, filter]) => (
          <Filter
            key={filterKey}
            data={filter}
            filterSection={groupKey}
            filterName={filterKey}
            isColorFilter={isColorFilter}
            onClick={(newState) => onSelectFilter(groupKey, filterKey, newState)}
          />
        ))}
      </div>
    </Accordion>
  );
};
